import { BarChart3, Car, CirclePlus, ClipboardCheck, ClipboardX, GripHorizontal, Home, List as ListIcon, ReceiptText, Settings, Tag, Truck, Wrench, X } from 'lucide-react';
import React, { useState } from 'react';
import { Dnd, ItemType } from 'src/@carketa/components/dnd/Dnd';
import { SideMenuValues } from 'src/types/organizations/organizations';
import { Button } from 'src/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from 'src/ui/dialog';
import { useMediaQuery } from 'src/ui/hooks/useMediaQuery';
import { breakpoints } from 'src/ui/theme/breakpoints';
import { Typography } from 'src/ui/typography';

interface CardItemType extends ItemType {
  id: string;
  text: string;
  color: string;
  icon?: any;
}

export const ReorderSideMenuDialog = ({
  open,
  handleClose,
  sideMenuOrder,
  sideMenuOrderData,
}: {
  open: boolean;
  handleClose: () => void;
  sideMenuOrder: (data: any) => void;
  sideMenuOrderData?: SideMenuValues;
}) => {
  const mainSideMenus = [
    {
      id: 'userOrganization',
      text: 'Dashboard',
      color: null,
      icon: <Home />,
    },
    {
      id: 'userWork',
      text: 'My Work',
      color: null,
      icon: <ClipboardCheck />,
    },
    {
      id: 'needsAttention',
      text: 'Needs Attention',
      color: null,
      icon: <ClipboardX />,
    },
    {
      id: 'userInventory',
      text: 'All Inventory',
      color: null,
      icon: <Car />,
    },
  ];

  const servicesMenu = [
    {
      id: 'vehicleSourcing',
      text: 'Auto Finder',
      color: null,
      icon: <Wrench />,
    },
    {
      id: 'userPricingAppraisals',
      text: 'Appraisals',
      color: null,
      icon: <ReceiptText />,
    },
    {
      id: 'vehicleTransportation',
      text: 'Transportation',
      color: null,
      icon: <Truck />,
    },
    {
      id: 'userRecon',
      text: 'Recon/Workflow',
      color: null,
      icon: <ListIcon />,
    },
    {
      id: 'userPricingInventory',
      text: 'Inventory Pricing',
      color: null,
      icon: <Tag />,
    },
    {
      id: 'userMarketReport',
      text: 'Market Report',
      color: null,
      icon: <BarChart3 />,
    },
    {
      id: 'userKSLDealerInsights',
      text: 'KSL Dealer Insights',
      color: null,
      icon: <BarChart3 />,
    },
    {
      id: 'reports',
      text: 'Reports',
      color: null,
      icon: <BarChart3 />,
    },
    {
      id: 'userSettings',
      text: 'Settings',
      color: null,
      icon: <Settings />,
    },
    {
      id: 'addProducts',
      text: 'Add Products',
      icon: <CirclePlus />,
      color: null,
    },
  ];

  const sortMenus = (menus: any, defaultMenus: any) => {
    if (!menus) return defaultMenus;

    // Create a mapping of id to order
    const orderMap = menus.reduce((map: any, link: any) => {
      map[link.id] = link.order;
      return map;
    }, {});

    // Sort mainSideMenus based on the orderMap
    const sortedMainSideMenus = defaultMenus.sort(
      (a: any, b: any) => orderMap[a.id] - orderMap[b.id]
    );

    return sortedMainSideMenus;
  };

  const [items, setItems] = useState(
    sortMenus(sideMenuOrderData?.services, servicesMenu)
  );
  const [mainItems, setMainItems] = useState(
    sortMenus(sideMenuOrderData?.mainLinks, mainSideMenus)
  );

  const handleSave = () => {
    const ids = items?.map((item: any, index: number) => {
      return {
        id: item.id,
        order: index,
        name: item.text,
      };
    });

    const mainIds = mainItems?.map((item: any, index: number) => {
      return {
        id: item.id,
        order: index,
        name: item.text,
      };
    });

    sideMenuOrder({
      mainLinks: mainIds,
      services: ids,
    });
    handleClose();
  };

  const handleCancel = () => {
    setMainItems(
      sortMenus(sideMenuOrderData ? sideMenuOrderData.mainLinks : null, mainSideMenus)
    );
    setItems(sortMenus(sideMenuOrderData ? sideMenuOrderData.services : null, servicesMenu));
    handleClose();
  };

  const smBelow = useMediaQuery(`(max-width: ${breakpoints.values.sm - 1}px)`);

  console.log('mainItems-->', mainItems);

  const renderMenuItem = (item: ItemType) => {
    const itemData = item as CardItemType;
    return (
      <div
        key={itemData.id}
        className="mb-2 flex cursor-pointer items-center rounded-md bg-primary px-3 py-2"
      >
        <div className="mr-2 text-white">
          <GripHorizontal />
        </div>
        <div className="flex items-center gap-2 text-white">
          {itemData.icon}
          <Typography variant="body2" className="text-white">
            {itemData.text}
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogContent
        fullWidth
        fullScreen={smBelow}
        overflow
        scroll
        title="Reorder side menu"
      >
        <div className="relative pr-8">
          <DialogTitle>Reorder side menu</DialogTitle>
          <button
            type="button"
            aria-label="close"
            onClick={handleCancel}
            className="absolute right-0 top-0 rounded-sm p-1 text-muted-foreground opacity-70 transition-opacity hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <Dnd
          items={mainItems}
          setItems={(items) => setMainItems(items as CardItemType[])}
          renderContainer={(items: React.ReactNode) => {
            return <div>{items}</div>;
          }}
          renderItem={renderMenuItem}
        />
        <div className="mb-4 mt-2 flex items-center gap-3">
          <span className="text-sm text-[#B9B7BE]">Services</span>
          <div className="h-px flex-1 bg-border" />
        </div>
        <Dnd
          items={items}
          setItems={(items) => setItems(items as CardItemType[])}
          renderContainer={(items: React.ReactNode) => {
            return <div>{items}</div>;
          }}
          renderItem={renderMenuItem}
        />
        <DialogFooter>
          <Button variant="outlined" color="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
